<template>
  <div
    class="feedback-popup"
    :class="{
      'popup-form': !embedded,
      'feedback-popup--embedded': embedded,
      'feedback-popup--capture-hidden': captureHidden,
    }"
  >
    <div class="content-form-popup feedback-content">
      <header class="feedback-header">
        <button v-if="!embedded" class="feedback-close" type="button" aria-label="Zavrieť" @click="closePopup">
          <app-icon name="close" :size="24"/>
        </button>
        <span class="feedback-kicker">Pomôžte nám zlepšiť Bankárov</span>
        <h2>Nahlásiť problém</h2>
        <p>
          Napíšte, čo sa stalo. Aktuálnu stránku a technický kontext priložíme
          automaticky.
        </p>
      </header>

      <form @submit.prevent="submit">
        <label class="feedback-field">
          <span>Čo sa stalo? *</span>
          <textarea
            ref="message"
            v-model.trim="message"
            rows="7"
            maxlength="5000"
            required
            placeholder="Popíšte, čo ste robili, čo ste očakávali a čo sa zobrazilo..."
          ></textarea>
          <small>{{ message.length }} / 5 000</small>
        </label>

        <div class="feedback-context-card">
          <div>
            <strong>Kontext stránky</strong>
            <span>Priloží sa automaticky</span>
          </div>
          <p :title="pageUrl">{{ pageUrl }}</p>
          <div v-if="mortgageId || clientId" class="feedback-context-tags">
            <span v-if="mortgageId">Hypotéka #{{ mortgageId }}</span>
            <span v-if="clientId">Klient #{{ clientId }}</span>
          </div>
        </div>

        <section class="feedback-screenshot">
          <div class="feedback-screenshot-heading">
            <div>
              <strong>Screenshot</strong>
              <span>Voliteľný, uloží sa súkromne</span>
            </div>
            <button
              v-if="!screenshotFile"
              class="feedback-secondary"
              type="button"
              :disabled="capturing || saving"
              @click="captureScreenshot"
            >
              <span v-if="capturing">Pripravujem screenshot...</span>
              <span v-else>Odfotiť obrazovku</span>
            </button>
          </div>

          <div v-if="screenshotPreview" class="feedback-preview">
            <img :src="screenshotPreview" alt="Náhľad priloženého screenshotu"/>
            <button type="button" :disabled="saving" @click="removeScreenshot">
              Odobrať screenshot
            </button>
          </div>
          <p v-else class="feedback-screenshot-help">
            Po kliknutí vyberte aktuálnu kartu Bankári. Formulár sa pri zábere na chvíľu skryje.
          </p>

          <label v-if="!screenshotFile" class="feedback-upload">
            <span>Nahrať existujúci obrázok</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              :disabled="capturing || saving"
              @change="selectScreenshot"
            >
          </label>

          <p v-if="captureError" class="feedback-error" role="alert">{{ captureError }}</p>
        </section>

        <div v-if="submitErrors.length" class="feedback-error-list" role="alert">
          <strong>Feedback sa nepodarilo odoslať:</strong>
          <span v-for="(error, index) in submitErrors" :key="index">{{ error }}</span>
        </div>

        <footer class="feedback-footer">
          <button class="feedback-secondary" type="button" :disabled="saving" @click="closePopup">
            Zrušiť
          </button>
          <button class="feedback-primary" type="submit" :disabled="saving || !message">
            {{ saving ? "Odosielam..." : "Odoslať feedback" }}
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script>
import AppIcon from "@/components/shared/AppIcon.vue";
import {createUserFeedback} from "@/services/brokerService";

export default {
  name: "UserFeedbackPopUp",
  components: {AppIcon},
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: "",
      pageUrl: window.location.href,
      screenshotFile: null,
      screenshotPreview: null,
      capturing: false,
      captureHidden: false,
      captureError: "",
      captureErrorCode: "",
      saving: false,
      submitErrors: [],
    };
  },
  computed: {
    mortgageId() {
      const routeId = Number(this.$route?.params?.hypoteka);
      return Number.isInteger(routeId) && routeId > 0 ? routeId : null;
    },
    clientId() {
      const routeClientId = Number(
        this.$route?.params?.client || this.$route?.params?.klient
      );
      if (Number.isInteger(routeClientId) && routeClientId > 0) return routeClientId;

      if (!this.mortgageId) return null;
      const applicant = this.$store.getters["mortgageDetailState/getApplicant"];
      return applicant?.id || null;
    },
  },
  mounted() {
    this.$refs.message?.focus();
  },
  beforeDestroy() {
    this.revokeScreenshotPreview();
  },
  methods: {
    nextPaint() {
      return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    },
    waitForVideoFrame(video) {
      if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          cleanup();
          const error = new Error("Prehliadač neposkytol obraz zo zdieľanej plochy.");
          error.name = "CaptureFrameTimeout";
          reject(error);
        }, 6000);
        const cleanup = () => {
          window.clearTimeout(timeout);
          video.removeEventListener("loadeddata", onReady);
          video.removeEventListener("canplay", onReady);
          video.removeEventListener("error", onError);
        };
        const onReady = () => {
          if (!video.videoWidth || !video.videoHeight) return;
          cleanup();
          resolve();
        };
        const onError = () => {
          cleanup();
          reject(video.error || new Error("Video zo zdieľanej plochy sa nepodarilo načítať."));
        };
        video.addEventListener("loadeddata", onReady);
        video.addEventListener("canplay", onReady);
        video.addEventListener("error", onError);
      });
    },
    captureErrorMessage(error) {
      if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
        return "Záznam obrazovky nebol povolený alebo bol výber zrušený. Povoľte „Záznam obrazovky“ – kamera ani mikrofón nestačia.";
      }
      if (error?.name === "NotReadableError") {
        return "macOS alebo prehliadač blokuje záznam obrazovky. V Súkromie a bezpečnosť → Záznam obrazovky povoľte svoj prehliadač a reštartujte ho.";
      }
      if (error?.name === "AbortError") {
        return "Výber obrazovky bol prerušený. Skúste znova a vyberte aktuálnu kartu Bankári.";
      }
      if (error?.name === "CaptureFrameTimeout") {
        return "Prehliadač neposkytol obraz vybranej karty. Skúste vybrať celé okno alebo nahrajte existujúci obrázok.";
      }
      return `Screenshot sa nepodarilo vytvoriť${error?.name ? ` (${error.name})` : ""}. Môžete nahrať obrázok alebo odoslať feedback bez neho.`;
    },
    async captureScreenshot() {
      this.captureError = "";
      this.captureErrorCode = "";
      if (!navigator.mediaDevices?.getDisplayMedia) {
        this.captureError = "Tento prehliadač nevie vytvoriť screenshot. Feedback môžete odoslať aj bez neho.";
        this.captureErrorCode = "DisplayMediaUnavailable";
        return;
      }

      let stream = null;
      this.capturing = true;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({video: true, audio: false});
        this.captureHidden = true;
        this.$emit("capture-state", true);
        await this.$nextTick();
        await this.nextPaint();

        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        await video.play();
        await this.waitForVideoFrame(video);
        await this.nextPaint();

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          throw new Error("Zdieľaná obrazovka nemá platný rozmer.");
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.86));
        if (!blob) throw new Error("Screenshot sa nepodarilo vytvoriť.");

        this.removeScreenshot();
        this.screenshotFile = new File(
          [blob],
          `bankari-feedback-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`,
          {type: "image/jpeg"}
        );
        this.screenshotPreview = URL.createObjectURL(blob);
      } catch (error) {
        this.captureErrorCode = error?.name || "UnknownCaptureError";
        this.captureError = this.captureErrorMessage(error);
      } finally {
        stream?.getTracks().forEach(track => track.stop());
        this.captureHidden = false;
        this.$emit("capture-state", false);
        this.capturing = false;
      }
    },
    selectScreenshot(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      this.captureError = "";
      this.captureErrorCode = "";
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        this.captureError = "Nahrajte PNG, JPG alebo WEBP obrázok.";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.captureError = "Obrázok môže mať najviac 10 MB.";
        return;
      }
      this.removeScreenshot();
      this.screenshotFile = file;
      this.screenshotPreview = URL.createObjectURL(file);
    },
    revokeScreenshotPreview() {
      if (this.screenshotPreview) URL.revokeObjectURL(this.screenshotPreview);
    },
    removeScreenshot() {
      this.revokeScreenshotPreview();
      this.screenshotPreview = null;
      this.screenshotFile = null;
    },
    contextPayload() {
      return {
        route: {
          name: this.$route?.name || "",
          params: this.$route?.params || {},
          query: this.$route?.query || {},
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          pixelRatio: window.devicePixelRatio || 1,
        },
        browser: {
          userAgent: navigator.userAgent,
          language: navigator.language,
        },
        screenshotCapture: {
          attached: Boolean(this.screenshotFile),
          error: this.captureErrorCode || null,
        },
        capturedAt: new Date().toISOString(),
      };
    },
    flattenErrors(data) {
      if (!data) return ["Skúste to, prosím, znova."];
      if (Array.isArray(data)) return data.flatMap(value => this.flattenErrors(value));
      if (typeof data === "object") {
        return Object.values(data).flatMap(value => this.flattenErrors(value));
      }
      return [String(data)];
    },
    async submit() {
      if (!this.message || this.saving) return;
      this.saving = true;
      this.submitErrors = [];

      const formData = new FormData();
      formData.append("message", this.message);
      formData.append("page_url", this.pageUrl);
      formData.append("route_name", this.$route?.name || "");
      formData.append("context", JSON.stringify(this.contextPayload()));
      if (this.mortgageId) formData.append("mortgage", this.mortgageId);
      if (this.clientId) formData.append("client", this.clientId);
      if (this.screenshotFile) formData.append("screenshot", this.screenshotFile);

      try {
        const feedback = await createUserFeedback(formData);
        this.$store.commit("appState/setSuccessMessage", {
          text: `Feedback #${feedback.id} bol uložený aj s kontextom stránky.`,
          type: "success",
        });
        this.saving = false;
        this.closePopup();
      } catch (error) {
        this.submitErrors = this.flattenErrors(error?.response?.data);
      } finally {
        this.saving = false;
      }
    },
    closePopup() {
      if (this.saving) return;
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
  },
};
</script>

<style scoped lang="scss">
.feedback-popup {
  width: min(520px, 100vw);
  background: var(--app-background);
  transition: none;
}

.feedback-popup--capture-hidden {
  opacity: 0;
  pointer-events: none;
}

.feedback-popup--embedded {
  width: 100%;
  background: transparent;
}

.feedback-popup--embedded .feedback-content {
  min-height: 0;
  padding: 2px 0 34px;
}

.feedback-content {
  min-height: 100%;
  padding: 30px;
  color: var(--app-text);
}

.feedback-header {
  position: relative;
  padding-right: 40px;
}

.feedback-header h2 {
  margin: 5px 0 8px;
  font-size: 28px;
  line-height: 1.2;
}

.feedback-header p {
  margin: 0 0 24px;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 1.55;
}

.feedback-kicker {
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.feedback-close {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  cursor: pointer;
}

.feedback-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feedback-field > span,
.feedback-context-card strong,
.feedback-screenshot strong {
  font-size: 14px;
  font-weight: 700;
}

.feedback-field textarea {
  box-sizing: border-box;
  width: 100%;
  resize: vertical;
  min-height: 150px;
  padding: 14px;
  border: 1px solid var(--app-border);
  border-radius: 14px;
  background: var(--app-surface);
  color: var(--app-text);
  font: inherit;
  line-height: 1.5;
}

.feedback-field textarea:focus {
  border-color: var(--app-primary);
  outline: 2px solid color-mix(in srgb, var(--app-primary) 20%, transparent);
}

.feedback-field small {
  align-self: flex-end;
  color: var(--app-text-muted);
}

.feedback-context-card,
.feedback-screenshot {
  margin-top: 18px;
  padding: 16px;
  border: 1px solid var(--app-border);
  border-radius: 16px;
  background: var(--app-surface-muted);
}

.feedback-context-card > div:first-child,
.feedback-screenshot-heading,
.feedback-screenshot-heading > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.feedback-context-card span,
.feedback-screenshot-heading span {
  color: var(--app-text-muted);
  font-size: 12px;
}

.feedback-context-card p {
  overflow: hidden;
  margin: 12px 0 0;
  color: var(--app-text-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-context-tags {
  display: flex;
  justify-content: flex-start !important;
  gap: 8px !important;
  margin-top: 12px;
}

.feedback-context-tags span {
  padding: 5px 9px;
  border-radius: 8px;
  background: var(--app-surface);
  color: var(--app-primary);
  font-weight: 700;
}

.feedback-screenshot-heading > div {
  align-items: flex-start;
  flex-direction: column;
  gap: 2px;
}

.feedback-preview {
  margin-top: 14px;
}

.feedback-preview img {
  display: block;
  width: 100%;
  max-height: 230px;
  object-fit: contain;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: #0d1727;
}

.feedback-preview button {
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--app-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.feedback-screenshot-help,
.feedback-error {
  margin: 12px 0 0;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.feedback-upload {
  display: inline-flex;
  margin-top: 9px;
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.feedback-upload input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.feedback-upload:focus-within {
  outline: 2px solid var(--app-primary);
  outline-offset: 3px;
}

.feedback-error,
.feedback-error-list {
  color: #e56b78;
}

.feedback-error-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 18px;
  padding: 14px;
  border: 1px solid rgba(229, 107, 120, .45);
  border-radius: 12px;
  background: rgba(229, 107, 120, .08);
  font-size: 12px;
}

.feedback-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.feedback-primary,
.feedback-secondary {
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.feedback-primary {
  border: 1px solid var(--app-primary);
  background: var(--app-primary);
  color: white;
}

.feedback-secondary {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-primary);
}

.feedback-primary:disabled,
.feedback-secondary:disabled {
  cursor: not-allowed;
  opacity: .55;
}

@media (max-width: 600px) {
  .feedback-content { padding: 22px 18px 100px; }
  .feedback-screenshot-heading { align-items: stretch; flex-direction: column; }
  .feedback-secondary { width: 100%; }
  .feedback-footer { flex-direction: column-reverse; }
  .feedback-primary { width: 100%; }
}
</style>
